import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    type PayinAdminTicketV1ResolveTicketRequest,
    type AdminPayinTicketServiceListTicketsParams,
} from '~/generated/payinBackend/model';
import {
    adminPayinTicketServiceListTickets,
    adminPayinTicketServiceResolveTicket,
    getAdminPayinTicketServiceListTicketsUrl,
} from '~/generated/payinBackend/ticket';
import { type TicketStatus } from '../model';

type TicketsParams = Omit<AdminPayinTicketServiceListTicketsParams, 'filter.statuses'> & {
    'filter.statuses'?: TicketStatus[];
};

export const useTicketsList = (params?: TicketsParams, refetchInterval?: number | false) => {
    return useQuery({
        refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayinTicketServiceListTickets(params as AdminPayinTicketServiceListTicketsParams),
        queryKey: [
            queryKeys.PAYIN_TICKETS,
            getAdminPayinTicketServiceListTicketsUrl(params as AdminPayinTicketServiceListTicketsParams),
        ],
    });
};

interface UseTicketMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useResolveTicket = (options?: UseTicketMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminTicketV1ResolveTicketRequest) => {
            return await adminPayinTicketServiceResolveTicket(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_TICKETS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Тикет закрыт',
                    });
                });
        },
    });
};
